/** Response Assign tool (Page182 Soft≠Live). Soft≠Live.
 * Multi-select units · kind chips (human / drone / robot).
 */
import { useState, useMemo, useEffect } from "react";
import { html, Ico, Alert, CraftSelect, go, stay, hashSearchParams, handoffKnownParamsOf } from "./ui.js";
import { api, path, useApi } from "./api.js";
import { isLocalHost } from "./pathseal.js";
import {
  candidateRowsOf,
  softResponseErrorOf,
} from "./response-live.js";
import {
  softDevicesClone,
  softRankForDevice,
} from "./response-soft-demo.js";
import { softStampDispatch, softTaskChipForDevice } from "./response-soft-sync.js";
export { softResponseErrorOf };

function safeAssignPath(op, opts) {
  try {
    return path(op, opts);
  } catch {
    if (op === "get__response_assign_status") return "/response/assign/status";
    if (op === "get__devices") {
      const lim = (opts && opts.query && opts.query.limit) || 80;
      return `/devices?limit=${encodeURIComponent(lim)}`;
    }
    if (op === "post__responder-dispatches_preview") return "/responder-dispatches/preview";
    if (op === "post__responder-dispatches") return "/responder-dispatches";
    if (op === "post__response_hybrid_runs") return "/response/hybrid/runs";
    return "";
  }
}

export function hybridRunDoneOf(run) {
  const status = String((run && run.status) || "");
  return ["materialized", "blocked", "compensated", "cancelled", "awaiting_approval"].includes(status);
}

async function pollHybridRun(runId, onRun) {
  const deadline = Date.now() + 60000;
  while (Date.now() < deadline) {
    const out = await api(`/response/hybrid/runs/${encodeURIComponent(runId)}`);
    const run = out && out.run;
    if (run) onRun(run);
    if (hybridRunDoneOf(run)) return run;
    await new Promise((resolve) => setTimeout(resolve, 800));
  }
  throw new Error("Hybrid run is still processing; check Response Board");
}

/** Pure: seat chips from Live assign posture — fail-closed. */
export function responseAssignSeatOf(post) {
  const p = post && typeof post === "object" ? post : {};
  const lim = (p.limits && typeof p.limits === "object") ? p.limits : {};
  const dMax = Number(lim.device_max);
  const dDef = Number(lim.device_default);
  const cMax = Number(lim.candidate_max);
  const cDef = Number(lim.candidate_default);
  return {
    registerAllowed: p.register_allowed === true,
    policyReadAllowed: p.policy_read_allowed === true,
    previewAllowed: p.preview_allowed === true,
    createAllowed: p.create_allowed === true,
    reReadAllowed: p.re_read_allowed === true,
    role: String(p.principal_role || ""),
    detail: p.detail || null,
    enabled: p.enabled === true,
    deviceLimit:
      Number.isFinite(dDef) && dDef > 0
        ? Math.min(dDef, Number.isFinite(dMax) && dMax > 0 ? dMax : 200)
        : 80,
    candidateLimit:
      Number.isFinite(cDef) && cDef > 0
        ? Math.min(cDef, Number.isFinite(cMax) && cMax > 0 ? cMax : 24)
        : 8,
  };
}

/** Pure: paint bound for 100k device strip — no Soft invent beyond source. */
export function assignDevicesWithFixOf(pack, maxN) {
  const root = pack && typeof pack === "object" ? pack : {};
  const items = root.items || root.devices || [];
  const rows = Array.isArray(items) ? items : [];
  const n = Number(maxN);
  const cap = Number.isFinite(n) && n > 0 ? Math.floor(n) : 80;
  return rows
    .filter((d) => !!(d && deviceMapFixOf(d)))
    .slice(0, cap);
}

/** Full register ids (fix or not) — handoff must match register, not first-with-fix. */
export function assignRegisterIdsOf(pack) {
  const root = pack && typeof pack === "object" ? pack : {};
  const items = root.items || root.devices || [];
  const rows = Array.isArray(items) ? items : [];
  return rows
    .map((d) => String((d && (d.device_id || d.id)) || "").trim())
    .filter(Boolean);
}

/** P1-HANDOFF-1: ?device= selects when present; missing after ready is honest miss — never first device. */
export function assignHandoffPickOf(wanted, registerIds, registerReady) {
  const w = String(wanted || "").trim();
  if (!w) return { mode: "none", deviceId: "", miss: false };
  if (!registerReady) return { mode: "pending", deviceId: "", miss: false };
  const ids = Array.isArray(registerIds) ? registerIds : [];
  if (ids.includes(w)) return { mode: "hit", deviceId: w, miss: false };
  return { mode: "miss", deviceId: "", miss: true };
}

export const ASSIGN_KIND_FILTERS = [
  { id: "all", label: "All" },
  { id: "human", label: "Human" },
  { id: "drone", label: "Drone" },
  { id: "robot", label: "Robot" },
  { id: "iot", label: "IoT" },
];

/** Pure: normalize unit kind for Assign chips. */
export function assignUnitKindOf(row) {
  const raw = String((row && (row.kind || row.unit_kind || row.asset_class)) || "human").toLowerCase();
  if (raw === "drone" || raw === "uav") return "drone";
  if (raw === "robot" || raw === "ugv" || raw === "bot") return "robot";
  if (raw === "iot" || raw === "automation") return "iot";
  if (raw === "other") return "other";
  return "human";
}

export function assignKindLabelOf(kind) {
  const k = String(kind || "human");
  if (k === "drone") return "Drone";
  if (k === "robot") return "Robot";
  if (k === "iot") return "IoT";
  if (k === "other") return "Other";
  return "Human";
}

/** Pure: filter candidates by kind chip. */
export function assignFilterByKindOf(rows, kindFilter) {
  const list = Array.isArray(rows) ? rows : [];
  const f = String(kindFilter || "all");
  if (f === "all") return list;
  return list.filter((r) => assignUnitKindOf(r) === f);
}

/** Pure: count chosen by kind for summary. */
export function assignKindTallyOf(rows) {
  const tally = { human: 0, drone: 0, robot: 0, iot: 0, other: 0 };
  for (const r of (Array.isArray(rows) ? rows : [])) {
    const k = assignUnitKindOf(r);
    if (tally[k] != null) tally[k] += 1;
    else tally.other += 1;
  }
  return tally;
}

const whyNot = (c) => (c.busy ? "already on a job"
  : !c.on ? "not on call"
  : c.stale ? "position too old"
  : "not available");

function deviceLabelOf(d) {
  if (!d) return "";
  const id = String(d.device_id || d.id || "");
  return String(d.display_label || d.label || d.name || d.map_label || id).trim() || id;
}

/** Live GET /devices paints map_lat/map_lng (not lat/lng). Soft≠Live: never invent a pin. */
export function deviceMapFixOf(d) {
  if (!d || typeof d !== "object") return null;
  const lat = Number(
    d.map_lat ?? d.lat ?? d.latitude ?? (Array.isArray(d.ll) ? d.ll[1] : undefined),
  );
  const lng = Number(
    d.map_lng ?? d.lng ?? d.longitude ?? (Array.isArray(d.ll) ? d.ll[0] : undefined),
  );
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
  if (Math.abs(lat) > 90 || Math.abs(lng) > 180) return null;
  return { lat, lng };
}

function toggleId(list, id) {
  const cur = Array.isArray(list) ? list : [];
  return cur.includes(id) ? cur.filter((x) => x !== id) : cur.concat([id]);
}

export function Assign({ toast }) {
  const posture = useApi(safeAssignPath("get__response_assign_status"));
  const post = posture.data || {};
  const seat = responseAssignSeatOf(posture.loading ? null : post);
  const devices = useApi(
    seat.registerAllowed
      ? safeAssignPath("get__devices", { query: { limit: seat.deviceLimit } })
      : "",
  );
  const siteAuth = useApi(
    seat.previewAllowed || seat.registerAllowed
      ? "/resource-fabric/site-authority/status"
      : "",
  );
  const sitesLive = useApi(
    !posture.loading && siteAuth.data && siteAuth.data.armed === true
      ? "/resource-fabric/sites"
      : "",
  );
  const liveFix = useMemo(
    () => (seat.registerAllowed ? assignDevicesWithFixOf(devices.data, seat.deviceLimit) : []),
    [devices.data, seat.registerAllowed, seat.deviceLimit],
  );
  const [forceSoft, setForceSoft] = useState(false);
  const useSoft = isLocalHost() && forceSoft;
  const siteGap = !useSoft
    && siteAuth.data
    && siteAuth.data.armed === true
    && !sitesLive.loading
    && Array.isArray(sitesLive.data && sitesLive.data.items)
    && sitesLive.data.items.length === 0;
  const withFix = useSoft ? softDevicesClone() : liveFix;

  const [deviceId, setDeviceId] = useState("");
  const [sels, setSels] = useState([]);
  const [kindFilter, setKindFilter] = useState("all");
  const [reason, setReason] = useState("CMC Response · Soft assign");
  const [showReason, setShowReason] = useState(false);
  const [preview, setPreview] = useState(null);
  const [softCands, setSoftCands] = useState([]);
  const [previewErr, setPreviewErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [hybridRun, setHybridRun] = useState(null);
  const [hybridIdempotencyKey, setHybridIdempotencyKey] = useState("");
  const [handoffMiss, setHandoffMiss] = useState(false);
  const handoffParams = hashSearchParams();
  const handoff = handoffKnownParamsOf(handoffParams);
  const wantedDevice = String(handoff.device || "").trim();
  const incidentId = String(
    (handoffParams && handoffParams.get && (
      handoffParams.get("incident") || handoffParams.get("alarm_id")
    )) || "",
  ).trim();
  const correlationId = String(
    (handoffParams && handoffParams.get && handoffParams.get("correlation_id")) || "",
  ).trim();
  const registerIds = useMemo(
    () => (useSoft ? withFix.map((d) => String(d.device_id || d.id || "")) : assignRegisterIdsOf(devices.data)),
    [useSoft, withFix, devices.data],
  );
  const registerReady = useSoft ? true : !!(seat.registerAllowed && !devices.loading);

  useEffect(() => {
    const pick = assignHandoffPickOf(wantedDevice, registerIds, registerReady);
    if (pick.mode === "none") {
      setHandoffMiss(false);
      if (!deviceId && withFix.length) {
        setDeviceId(String(withFix[0].device_id || withFix[0].id || ""));
      }
      return;
    }
    if (pick.mode === "pending") return;
    setHandoffMiss(pick.miss);
    if (pick.mode === "hit" && pick.deviceId && pick.deviceId !== deviceId) {
      setDeviceId(pick.deviceId);
    }
  }, [wantedDevice, registerIds, registerReady, withFix, deviceId]);

  const selectedDev = withFix.find((d) => String(d.device_id || d.id) === deviceId) || null;
  const candidates = useMemo(() => {
    if (useSoft) return softCands.slice(0, seat.candidateLimit);
    const rows = candidateRowsOf(preview, deviceId);
    return rows.slice(0, seat.candidateLimit);
  }, [useSoft, softCands, preview, deviceId, seat.candidateLimit]);
  const rankConflict = !useSoft && (
    (preview && preview.rank_conflict === true)
    || (preview && preview.needs_you === true)
    || candidates.some((c) => c && (c.rankConflict || c.needsYou))
  );
  const visible = useMemo(
    () => assignFilterByKindOf(candidates, kindFilter),
    [candidates, kindFilter],
  );
  const chosen = useMemo(
    () => candidates.filter((c) => c.eligible && sels.includes(c.id)),
    [candidates, sels],
  );
  const tally = assignKindTallyOf(chosen);
  const deviceName = deviceLabelOf(selectedDev) || deviceId || "—";
  const kindCounts = useMemo(() => {
    const t = { all: candidates.length, human: 0, drone: 0, robot: 0, iot: 0, other: 0 };
    for (const c of candidates) {
      const k = assignUnitKindOf(c);
      if (t[k] != null) t[k] += 1;
      else t.other += 1;
    }
    return t;
  }, [candidates]);

  const pickDevice = (id) => {
    setDeviceId(id);
    setPreview(null);
    setSoftCands([]);
    setSels([]);
    setKindFilter("all");
    setPreviewErr("");
    setHybridRun(null);
    setHybridIdempotencyKey("");
  };

  const reReadAll = () => {
    posture.reload && posture.reload();
    devices.reload && devices.reload();
    toast(useSoft ? "Soft devices ready" : "Assign list refreshed", "go");
  };

  const runPreview = async () => {
    if (!selectedDev) {
      toast("Pick a device that has a map location", "warn");
      return;
    }
    if (useSoft) {
      const rows = softRankForDevice(deviceId).slice(0, seat.candidateLimit);
      setSoftCands(rows);
      setPreview({ soft_demo: true, candidates: rows });
      const first = rows.find((c) => c.eligible);
      setSels(first ? [first.id] : []);
      setKindFilter("all");
      toast(rows.length ? ("Soft ranked · " + rows.length + " units · not Live") : "Soft · no units", "warn");
      return;
    }
    if (!seat.previewAllowed) {
      toast("Finding nearest units needs a manager seat", "warn");
      return;
    }
    const fix = deviceMapFixOf(selectedDev);
    const lat = fix ? fix.lat : NaN;
    const lng = fix ? fix.lng : NaN;
    setBusy(true);
    setPreviewErr("");
    try {
      const out = await api(safeAssignPath("post__responder-dispatches_preview"), {
        method: "POST",
        body: {
          latitude: lat,
          longitude: lng,
          device_id: deviceId,
          limit: seat.candidateLimit,
        },
      });
      setPreview(out);
      const rows = candidateRowsOf(out, deviceId).slice(0, seat.candidateLimit);
      const first = rows.find((c) => c.eligible);
      setSels(first ? [first.id] : []);
      setKindFilter("all");
      toast(rows.length ? ("Found " + rows.length + " nearby") : "No units ranked", "go");
    } catch (e) {
      setPreview(null);
      setPreviewErr(softResponseErrorOf((e && e.message) || e || "Could not rank units"));
      toast(softResponseErrorOf((e && e.message) || e || "Could not rank units"), "bad");
    } finally {
      setBusy(false);
    }
  };

  const doDispatch = async () => {
    if (!chosen.length || !deviceId) {
      toast("Choose at least one eligible unit", "warn");
      return;
    }
    const bits = [];
    if (tally.human) bits.push(tally.human + " human");
    if (tally.drone) bits.push(tally.drone + " drone");
    if (tally.robot) bits.push(tally.robot + " robot");
    if (tally.iot) bits.push(tally.iot + " IoT");
    const mix = bits.length ? (" · " + bits.join(" · ")) : "";
    if (useSoft) {
      const stamp = softStampDispatch({
        deviceId,
        units: chosen.map((c) => ({
          id: String(c.id || ""),
          label: String(c.label || c.id || ""),
          kind: assignUnitKindOf(c),
        })),
        source: "assign-soft-send",
      });
      if (!stamp || !stamp.ok) {
        toast("Soft send needs a Soft device and at least one unit", "warn");
        return;
      }
      toast(
        "Soft send · task " + stamp.taskLabel + " · " + stamp.queueId
          + " · " + chosen.length + " unit" + (chosen.length === 1 ? "" : "s")
          + mix + " → " + deviceName + " · Queue + Map Soft sync · not Live",
        "warn",
      );
      stay(
        "/response/board?focus=" + encodeURIComponent(stamp.queueId)
          + "&return_to=" + encodeURIComponent("/response/assign"),
      );
      return;
    }
    if (!seat.createAllowed) {
      toast("Sending a dispatch needs a manager seat", "warn");
      return;
    }
    if (chosen.some((c) => assignUnitKindOf(c) === "other")) {
      toast("Live hybrid send supports human, robot / drone, and IoT units", "warn");
      return;
    }
    const hasMachine = chosen.some((c) => assignUnitKindOf(c) !== "human");
    if (hasMachine && !incidentId) {
      toast("Robot / IoT send requires a real incident context", "warn");
      return;
    }
    setBusy(true);
    try {
      const key = hybridIdempotencyKey || (
        (globalThis.crypto && globalThis.crypto.randomUUID)
          ? globalThis.crypto.randomUUID()
          : ("cmc-" + Date.now() + "-" + Math.random().toString(16).slice(2))
      );
      setHybridIdempotencyKey(key);
      const out = await api(safeAssignPath("post__response_hybrid_runs"), {
        method: "POST",
        headers: { "Idempotency-Key": key },
        body: {
          device_id: deviceId,
          incident_id: incidentId || undefined,
          correlation_id: correlationId || undefined,
          reason: reason || "CMC Response assign",
          risk_score: 0,
          units: chosen.map((unit) => {
            const sourceKind = assignUnitKindOf(unit);
            const machineKind = sourceKind === "drone" ? "robot" : sourceKind;
            return {
              id: unit.id,
              kind: machineKind,
              verb: machineKind === "robot" ? "goto" : undefined,
              lat: machineKind === "robot"
                ? Number(selectedDev.lat ?? selectedDev.latitude ?? (selectedDev.ll && selectedDev.ll[1]))
                : undefined,
              lng: machineKind === "robot"
                ? Number(selectedDev.lng ?? selectedDev.longitude ?? (selectedDev.ll && selectedDev.ll[0]))
                : undefined,
              plan_version: machineKind === "iot" ? unit.plan_version : undefined,
              resource_id: machineKind === "iot" ? (unit.resource_id || unit.id) : undefined,
              capability: machineKind === "iot" ? unit.capability : undefined,
              action_name: machineKind === "iot" ? unit.action_name : undefined,
              schema_version: machineKind === "iot" ? unit.schema_version : undefined,
              parameters: machineKind === "iot" ? unit.parameters : undefined,
            };
          }),
        },
      });
      const submitted = out && out.run;
      if (!submitted || !submitted.run_id) throw new Error("Hybrid run was not created");
      setHybridRun(submitted);
      const finalRun = hybridRunDoneOf(submitted)
        ? submitted
        : await pollHybridRun(submitted.run_id, setHybridRun);
      if (finalRun.status === "materialized") {
        toast("Run materialized · execution remains separately observed · " + chosen.length + " units → " + deviceName, "go");
        stay("/response/board?focus=" + encodeURIComponent(finalRun.run_id));
      } else if (finalRun.status === "awaiting_approval") {
        toast("Hybrid run is waiting for machine approval · " + finalRun.run_id, "warn");
      } else {
        toast("Hybrid run " + finalRun.status + " · no per-unit success claimed", "bad");
      }
    } catch (e) {
      toast(softResponseErrorOf((e && e.message) || e || "Hybrid dispatch refused"), "bad");
    } finally {
      setBusy(false);
    }
  };

  const sealed = !useSoft && !posture.loading && !seat.previewAllowed;
  const summarySay = chosen.length
    ? (chosen.length === 1
      ? html`<b>${chosen[0].label}</b> <abbr data-kind=${assignUnitKindOf(chosen[0])}>${assignKindLabelOf(assignUnitKindOf(chosen[0]))}</abbr> → <b>${deviceName}</b>`
      : html`<b>${chosen.length} units</b>${tally.human ? html` · ${tally.human} human` : null}${tally.drone ? html` · ${tally.drone} drone` : null}${tally.robot ? html` · ${tally.robot} robot` : null}${tally.iot ? html` · ${tally.iot} IoT` : null} → <b>${deviceName}</b>`)
    : html`<span>Multi-select eligible units above.</span>`;

  return html`
    <div className="rr-assign" data-cmc-residual="page182-assign" data-soft=${useSoft ? "1" : "0"}>
      <p className="rr-assign__lede">
        ${useSoft
          ? "Soft practice · multi-select humans, drones, robots. Soft send never writes Live."
          : sealed
            ? "You can look, but ranking and send need a manager seat."
            : "Three steps: pick the device, multi-select nearest units, send."}
      </p>
      ${isLocalHost() ? html`<div className="rr-assign__mode">
        <button type="button" data-on=${useSoft ? "1" : "0"}
          onClick=${() => { setForceSoft(true); setSoftCands([]); setPreview(null); setSels([]); }}>Soft practice</button>
        <button type="button" data-on=${!useSoft ? "1" : "0"}
          onClick=${() => { setForceSoft(false); setSoftCands([]); setPreview(null); setSels([]); }}>
          Live devices (${liveFix.length})
        </button>
      </div>` : html`<p className="rr-assign__live-count">Live devices (${liveFix.length})</p>`}

      ${!posture.loading && !seat.enabled
        ? html`<${Alert} tone="warn" title="Assign closed" say=${softResponseErrorOf(seat.detail || "Assign closed")}/>`
        : null}
      ${wantedDevice && handoffMiss
        ? html`<${Alert} tone="warn" title="That device is not on your list"
            say=${"Device " + wantedDevice + " is missing. Nothing was auto-picked."}/>`
        : null}
      ${wantedDevice && !handoffMiss && deviceId === wantedDevice
        ? html`<${Alert} tone="info" title="Device from Map"
            say=${"Confirm before send — Assign does not auto-dispatch."}/>`
        : null}
      ${!useSoft && incidentId
        ? html`<${Alert} tone="info" title="Live task context"
            say=${"This dispatch will link to incident " + incidentId
              + (correlationId ? (" · correlation " + correlationId) : "") + "."}/>`
        : null}
      ${!useSoft && hybridRun
        ? html`<${Alert} tone=${hybridRun.status === "materialized" ? "info" : "warn"}
            title=${"Hybrid run · " + hybridRun.status}
            say=${hybridRun.run_id + " · " + (hybridRun.steps || []).length + " coordinated steps · materialized ≠ executed"}/>`
        : null}
      ${devices.error ? html`<${Alert} tone="warn" title="Devices unavailable" say=${softResponseErrorOf(devices.error)}/>` : null}
      ${siteGap ? html`<${Alert} tone="warn" title="Site scope required"
          say=${softResponseErrorOf("site_membership_required")}/>` : null}
      ${previewErr ? html`<${Alert} tone="warn" title="Could not find units" say=${previewErr}/>` : null}
      ${rankConflict ? html`<${Alert} tone="warn" title="Needs You · Rank conflict"
          say=${"Overlapping Zone Rank policies disagree. Site fallback is not used while conflict stands — resolve Preferred crew / manager / mode on the rings, or pick a unit by hand."}/>` : null}

      <section className="rr-assign__step" data-step="1">
        <header><i>1</i><span>Which device needs help?</span></header>
        ${!useSoft && !seat.registerAllowed
          ? html`<p className="note">Device list is sealed for this account.</p>`
          : !withFix.length
            ? html`<p className="note">${devices.loading || posture.loading
              ? "Loading devices…"
              : "No device with a map location — pin a unit on the map first."}</p>`
            : html`
              <label className="rr-assign__field">
                <span>Device with map fix</span>
                <${CraftSelect}
                  className="rr-assign__select"
                  aria-label="Device with location"
                  value=${deviceId}
                  placeholder="Choose a device…"
                  options=${withFix.map((d) => {
                    const id = String(d.device_id || d.id || "");
                    return {
                      value: id,
                      label: deviceLabelOf(d),
                      hint: useSoft ? (id + " · Soft") : id,
                    };
                  })}
                  onChange=${(e) => pickDevice(e.target.value)}/>
              </label>
              <p className="rr-assign__hint">${(() => {
                const base = withFix.length + " device" + (withFix.length === 1 ? "" : "s")
                  + " with location" + (useSoft ? " · Soft" : "");
                if (!useSoft || !deviceId) return base;
                const chip = softTaskChipForDevice(deviceId);
                return chip
                  ? (base + " · task " + chip.label + " · " + chip.queueId)
                  : (base + " · Soft ad-hoc task on send");
              })()}</p>`}
        <button type="button" className="btn btn--go btn--sm rr-assign__primary"
          disabled=${!deviceId || busy || (!useSoft && !seat.previewAllowed)}
          onClick=${runPreview}>
          <${Ico} d="refresh" w=${13}/> ${busy && !preview ? "Finding…" : "Find nearest units"}
        </button>
        ${!useSoft && !seat.previewAllowed
          ? html`<p className="note">Finding units is sealed for this seat.</p>`
          : null}
      </section>

      <section className="rr-assign__step" data-step="2">
        <header><i>2</i><span>Who / what should go? · multi-select</span></header>
        ${!useSoft && !seat.previewAllowed
          ? html`<p className="note">Ranking is sealed.</p>`
          : !candidates.length
            ? html`<p className="note">${previewErr
              ? "Ranking did not complete."
              : (preview && !useSoft
                ? softResponseErrorOf(String((preview && preview.reason) || "no_fresh_phone_locations"))
                : "Tap Find nearest units after you pick a device.")}</p>`
            : html`
              <div className="rr-assign__kinds" role="tablist" aria-label="Unit kind">
                ${ASSIGN_KIND_FILTERS.map((f) => html`
                  <button key=${f.id} type="button" role="tab"
                    aria-selected=${kindFilter === f.id ? "true" : "false"}
                    data-on=${kindFilter === f.id ? "1" : "0"}
                    data-kind=${f.id}
                    onClick=${() => setKindFilter(f.id)}>
                    ${f.label}
                    <em>${f.id === "all" ? kindCounts.all : (kindCounts[f.id] || 0)}</em>
                  </button>`)}
              </div>
              ${!visible.length
                ? html`<p className="note">No ${assignKindLabelOf(kindFilter).toLowerCase()} units in this rank.</p>`
                : html`<div className="rr-assign__cands" role="listbox" aria-multiselectable="true"
                    aria-label="Nearest units">
                    ${visible.map((c) => {
                      const kind = assignUnitKindOf(c);
                      const on = sels.includes(c.id);
                      return html`
                        <button key=${c.id} type="button" className="cand" role="option"
                          aria-selected=${on ? "true" : "false"}
                          data-ok=${c.eligible ? "1" : "0"}
                          data-sel=${on ? "1" : "0"}
                          data-kind=${kind}
                          onClick=${() => {
                            if (c.eligible && (useSoft || seat.previewAllowed)) {
                              setSels((prev) => toggleId(prev, c.id));
                              setHybridRun(null);
                              setHybridIdempotencyKey("");
                            }
                          }}>
                          <b className="cand__r">${c.rank}</b>
                          <span className="cand__t">
                            <b>${c.label}</b>
                            <em>${c.eligible
                              ? ((c.km !== "—" ? (c.km + " km · ") : "") + assignKindLabelOf(kind))
                              : whyNot(c)}</em>
                          </span>
                          <abbr className="cand__kind" data-kind=${kind}>${assignKindLabelOf(kind)}</abbr>
                          <span className="cand__s">${c.eligible ? (on ? "in" : "add") : "skip"}</span>
                        </button>`;
                    })}
                  </div>`}
              <p className="rr-assign__hint">${sels.length} selected · tap again to remove</p>`}
      </section>

      <section className="rr-assign__step" data-step="3">
        <header><i>3</i><span>Send</span></header>
        <p className="rr-assign__summary">${summarySay}</p>
        <button type="button" className="btn btn--text btn--sm"
          onClick=${() => setShowReason((v) => !v)}>
          ${showReason ? "Hide note" : "Add a note (optional)"}
        </button>
        ${showReason ? html`
          <label className="rr-assign__reason">
            <span>Note on the dispatch</span>
            <input className="inp" value=${reason}
              disabled=${!useSoft && !seat.createAllowed}
              onChange=${(e) => setReason(e.target.value)}
              placeholder="Why this mix / this job"/>
          </label>` : null}
        <button type="button" className="btn btn--go rr-assign__primary"
          disabled=${!chosen.length || !deviceId || busy || (!useSoft && !seat.createAllowed)}
          onClick=${doDispatch}>
          ${busy && chosen.length
            ? "Sending…"
            : (chosen.length
              ? ((useSoft ? "Soft send " : "Send ") + chosen.length + " unit" + (chosen.length === 1 ? "" : "s"))
              : (useSoft ? "Soft send" : "Send"))}
        </button>
        ${!useSoft && !seat.createAllowed
          ? html`<p className="note">Send is sealed for this seat.</p>`
          : null}
        ${!useSoft
          ? html`<p className="note">Live send creates one coordinated run. Robot / IoT requires a real incident and approval.</p>`
          : null}
      </section>

      <footer className="rr-assign__foot">
        <button type="button" className="btn btn--ghost btn--sm"
          disabled=${!seat.reReadAllowed && !posture.loading}
          onClick=${reReadAll}>
          <${Ico} d="refresh" w=${13}/> Refresh list
        </button>
        <button type="button" className="btn btn--text btn--sm"
          onClick=${() => go("/settings/ai")}>
          AI auto-call → Settings
        </button>
      </footer>
    </div>
  `;
}
